import { useCallback, useEffect, useRef, useState } from 'react';

const META_INICIAL = {
    totalRowCount: 0,
    totalContados: 0,
    totalNaoContados: 0,
    totalConferidos: 0,
    totalAguardandoConferencia: 0,
    start: 0,
    size: 50,
};

const ESTADO_INICIAL = {
    contagemIniciada: false,
    conferenciaIniciada: false,
    conferenciaConcluida: false,
    aplicado: false,
    podeAlterarLista: false,
    podeEditarContagem: false,
    podeConferir: false,
    podeAplicar: false,
    podeExcluir: false,
};

export function useBalancoModule({
    dados,
    setDados,
    enabled = false,
    MovimentoAPI,
    setFlashMessage,
    consultaDatalistColaborador,
    initialPageSize = 50,
}) {
    const movimentoId = dados?.id || null;
    const movimentoUuid = dados?.uuid || null;

    const [isSavingCounts, setIsSavingCounts] = useState(false);
    const [isCheckingItems, setIsCheckingItems] = useState(false);
    const [isApplyingBalance, setIsApplyingBalance] = useState(false);

    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState(() => ({ ...META_INICIAL, size: initialPageSize }));
    const [estado, setEstado] = useState(ESTADO_INICIAL);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: initialPageSize,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isRefetching, setIsRefetching] = useState(false);
    const [isError, setIsError] = useState(false);

    const [contagensEditadas, setContagensEditadas] = useState({});

    const requestIdRef = useRef(0);
    const hasLoadedRef = useRef(false);
    const peopleRequestIdRef = useRef(0);

    const saveCountsLockRef = useRef(false);
    const saveCountsRequestIdRef = useRef(0);
    const checkItemsLockRef = useRef(false);
    const checkItemsRequestIdRef = useRef(0);
    const applyBalanceLockRef = useRef(false);
    const applyBalanceRequestIdRef = useRef(0);

    const [previewItems, setPreviewItems] = useState([]);
    const [previewMeta, setPreviewMeta] = useState({
        totalRowCount: 0,
    });
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [isPreviewError, setIsPreviewError] = useState(false);

    const [peopleOptions, setPeopleOptions] = useState([]);
    const [isPeopleLoading, setIsPeopleLoading] = useState(false);
    const [isPeopleError, setIsPeopleError] = useState(false);

    const configuracao = {
        categoria: 'produto',
        descricao: '',
        ...(dados?.configuracaoBalanco || {}),
    };

    const changeConfiguration = useCallback(
        (field, value) => {
            if (typeof setDados !== 'function') return;

            setDados((current) => ({
                ...current,
                configuracaoBalanco: {
                    categoria: 'produto',
                    descricao: '',
                    ...(current?.configuracaoBalanco || {}),
                    [field]: value,
                },
            }));
        },
        [setDados]
    );

    const preview = useCallback(async () => {
        if (!enabled || movimentoId) return null;

        const empresaId = Number(dados?.empresaId);
        const almoxarifadoId = Number(dados?.almoxarifadoId);
        const categoria = configuracao?.categoria || 'produto';
        const descricao = String(configuracao?.descricao || '').trim();

        if (!empresaId || !almoxarifadoId) {
            setFlashMessage?.('Selecione o almoxarifado no cabeçalho antes de consultar.', 'warning');

            return null;
        }

        if (typeof MovimentoAPI?.listarProdutosBalanco !== 'function') {
            setFlashMessage?.('A consulta de produtos do balanço não está configurada.', 'error');

            return null;
        }

        const filters = JSON.stringify(
            descricao
                ? [
                      {
                          id: 'descricao',
                          value: descricao,
                      },
                  ]
                : []
        );

        const filtersMode = JSON.stringify(
            descricao
                ? {
                      descricao: 'startsWith',
                  }
                : {}
        );

        const sorting = JSON.stringify([
            {
                id: 'descricao',
                desc: false,
            },
        ]);

        setIsPreviewLoading(true);
        setIsPreviewError(false);

        try {
            const response = await MovimentoAPI.listarProdutosBalanco({
                empresaId,
                almoxarifadoId,
                categoria,
                filters,
                filtersMode,
                sorting,
            });

            const payload = Array.isArray(response?.data?.data) ? response.data : response;

            const produtos = Array.isArray(payload?.data) ? payload.data : [];

            setPreviewItems(produtos);
            setPreviewMeta({
                totalRowCount: payload?.meta?.totalRowCount ?? produtos.length,
                ...(payload?.meta || {}),
            });

            if (produtos.length === 0) {
                setFlashMessage?.('O filtro informado não retornou produtos.', 'warning');
            }

            return payload;
        } catch (error) {
            setPreviewItems([]);
            setPreviewMeta({
                totalRowCount: 0,
            });
            setIsPreviewError(true);

            setFlashMessage?.(
                error?.response?.data?.message || error?.message || 'Erro ao consultar os produtos do balanço.',
                error?.response?.data?.type || 'error'
            );

            return null;
        } finally {
            setIsPreviewLoading(false);
        }
    }, [
        enabled,
        movimentoId,
        dados?.empresaId,
        dados?.almoxarifadoId,
        configuracao?.categoria,
        configuracao?.descricao,
        MovimentoAPI,
        setFlashMessage,
    ]);

    const changeCount = useCallback((item, value) => {
        const chave = item?.uuid ? `uuid:${item.uuid}` : item?.id ? `id:${item.id}` : null;

        if (!chave) return;

        const quantidade = String(value ?? '');

        // Vazio representa uma digitação ainda incompleta.
        // Zero continua sendo aceito normalmente.
        if (quantidade !== '' && !/^\d+(?:[.,]\d*)?$/.test(quantidade)) {
            return;
        }

        setContagensEditadas((current) => ({
            ...current,
            [chave]: {
                ...(current[chave] || {}),
                id: item?.id ?? null,
                uuid: item?.uuid ?? null,
                quantidade,
            },
        }));
    }, []);
    const changeCounter = useCallback((item, pessoa) => {
        const chave = item?.uuid ? `uuid:${item.uuid}` : item?.id ? `id:${item.id}` : null;

        if (!chave) return;

        setContagensEditadas((current) => ({
            ...current,
            [chave]: {
                ...(current[chave] || {}),
                ...(item?.contado === true && !Object.prototype.hasOwnProperty.call(current[chave] || {}, 'quantidade')
                    ? {
                          quantidade: String(item?.quantidade ?? ''),
                      }
                    : {}),
                id: item?.id ?? null,
                uuid: item?.uuid ?? null,
                pessoaId: pessoa?.id ?? null,
                Pessoa: pessoa
                    ? {
                          id: pessoa.id,
                          descricao: pessoa.descricao,
                      }
                    : null,
            },
        }));
    }, []);
    const changeChecker = useCallback((item, pessoa) => {
        const chave = item?.uuid ? `uuid:${item.uuid}` : item?.id ? `id:${item.id}` : null;

        if (!chave) return;

        setContagensEditadas((current) => ({
            ...current,
            [chave]: {
                ...(current[chave] || {}),
                ...(item?.contado === true && !Object.prototype.hasOwnProperty.call(current[chave] || {}, 'quantidade')
                    ? {
                          quantidade: String(item?.quantidade ?? ''),
                      }
                    : {}),
                id: item?.id ?? null,
                uuid: item?.uuid ?? null,
                colaboradorId: pessoa?.id ?? null,
                Colaborador: pessoa
                    ? {
                          id: pessoa.id,
                          descricao: pessoa.descricao,
                      }
                    : null,
            },
        }));
    }, []);
    const searchPeople = useCallback(
        async (value) => {
            const filtro = String(value ?? '').trim();

            // Invalida uma consulta anterior ainda em andamento.
            const requestId = ++peopleRequestIdRef.current;

            if (filtro.length < 2) {
                setPeopleOptions([]);
                setIsPeopleError(false);
                setIsPeopleLoading(false);
                return [];
            }

            if (typeof consultaDatalistColaborador !== 'function') {
                setPeopleOptions([]);
                setIsPeopleError(true);
                setFlashMessage?.('A pesquisa de pessoas do balanço não está configurada.', 'error');
                return [];
            }

            setIsPeopleLoading(true);
            setIsPeopleError(false);

            try {
                const response = await consultaDatalistColaborador({
                    empresaId: dados?.empresaId,
                    filtro,
                    ativo: true,
                    tipo: null,
                });

                if (requestId !== peopleRequestIdRef.current) {
                    return [];
                }

                const result = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];

                const options = result.map((pessoa) => ({
                    ...pessoa,
                    valorSelecao: `${pessoa.descricao}`,
                }));

                setPeopleOptions(options);

                return options;
            } catch (error) {
                if (requestId !== peopleRequestIdRef.current) {
                    return [];
                }

                setPeopleOptions([]);
                setIsPeopleError(true);

                setFlashMessage?.(
                    error?.response?.data?.message || error?.message || 'Erro ao pesquisar pessoas para o balanço.',
                    error?.response?.data?.type || 'error'
                );

                return [];
            } finally {
                if (requestId === peopleRequestIdRef.current) {
                    setIsPeopleLoading(false);
                }
            }
        },
        [consultaDatalistColaborador, dados?.empresaId, setFlashMessage]
    );

    const fetchItems = useCallback(async () => {
        if (!enabled || (!movimentoId && !movimentoUuid)) return null;

        if (typeof MovimentoAPI?.listarItensBalanco !== 'function') {
            setIsError(true);
            setFlashMessage?.('A consulta dos itens do balanço não está configurada.', 'error');
            return null;
        }

        const requestId = ++requestIdRef.current;
        const primeiraCarga = !hasLoadedRef.current;

        setIsError(false);
        setIsLoading(primeiraCarga);
        setIsRefetching(!primeiraCarga);

        try {
            const response = await MovimentoAPI.listarItensBalanco({
                movimentoId,
                movimentoUuid,
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
            });

            if (requestId !== requestIdRef.current) return null;

            // Compatível tanto com a resposta Axios quanto com uma API que
            // já devolva diretamente o corpo da resposta.
            const payload = Array.isArray(response?.data?.data) ? response.data : response;
            const nextItems = Array.isArray(payload?.data) ? payload.data : [];

            setItems(nextItems);
            setMeta({ ...META_INICIAL, size: pagination.pageSize, ...(payload?.meta || {}) });
            setEstado({ ...ESTADO_INICIAL, ...(payload?.estado || {}) });
            hasLoadedRef.current = true;

            return payload;
        } catch (error) {
            if (requestId !== requestIdRef.current) return null;

            setIsError(true);
            setFlashMessage?.(
                error?.response?.data?.message || error?.message || 'Erro ao carregar os itens do balanço.',
                error?.response?.data?.type || 'error'
            );

            return null;
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
                setIsRefetching(false);
            }
        }
    }, [enabled, movimentoId, movimentoUuid, pagination.pageIndex, pagination.pageSize, MovimentoAPI, setFlashMessage]);

    const saveCounts = useCallback(async () => {
        if (saveCountsLockRef.current || checkItemsLockRef.current || applyBalanceLockRef.current) return null;

        if (estado.podeEditarContagem !== true) {
            setFlashMessage?.('Este balanço não permite alterar contagens.', 'warning');
            return null;
        }

        if (typeof MovimentoAPI?.salvarContagemBalanco !== 'function') {
            setFlashMessage?.('O salvamento das contagens não está configurado.', 'error');
            return null;
        }

        const entries = Object.entries(contagensEditadas).filter(([, item]) =>
            Object.prototype.hasOwnProperty.call(item, 'quantidade')
        );

        if (entries.length === 0) {
            setFlashMessage?.('Nenhuma contagem foi alterada.', 'warning');
            return null;
        }

        const possuiQuantidadeVazia = entries.some(([, item]) => String(item?.quantidade ?? '').trim() === '');

        if (possuiQuantidadeVazia) {
            setFlashMessage?.('Existem contagens vazias. Informe a quantidade ou restaure o valor anterior.', 'warning');
            return null;
        }

        const itens = [];

        for (const [, draft] of entries) {
            const quantidade = Number(String(draft.quantidade).trim().replace(',', '.'));

            if (!Number.isFinite(quantidade) || quantidade < 0) {
                setFlashMessage?.('Existe uma quantidade inválida na contagem.', 'warning');
                return null;
            }

            const itemPayload = {
                id: draft.id ?? null,
                uuid: draft.uuid ?? null,
                quantidade,
            };

            if (Object.prototype.hasOwnProperty.call(draft, 'pessoaId')) {
                itemPayload.pessoaId = draft.pessoaId;
            }

            if (Object.prototype.hasOwnProperty.call(draft, 'colaboradorId')) {
                itemPayload.colaboradorId = draft.colaboradorId;
            }

            itens.push(itemPayload);
        }

        const requestId = ++saveCountsRequestIdRef.current;
        const draftsEnviados = new Map(entries);

        saveCountsLockRef.current = true;
        setIsSavingCounts(true);

        try {
            const response = await MovimentoAPI.salvarContagemBalanco({
                movimentoId,
                movimentoUuid,
                itens,
            });

            if (requestId !== saveCountsRequestIdRef.current) {
                return null;
            }

            const responseBody = response?.data ?? response;
            const resultado = responseBody?.data ?? responseBody;

            // Remove somente o rascunho que efetivamente foi enviado.
            setContagensEditadas((current) => {
                const next = { ...current };

                draftsEnviados.forEach((draftEnviado, chave) => {
                    if (current[chave] === draftEnviado) {
                        delete next[chave];
                    }
                });

                return next;
            });

            if (resultado?.meta) {
                setMeta((current) => ({
                    ...current,
                    totalRowCount: Number(resultado.meta.totalItens ?? current.totalRowCount),
                    totalContados: Number(resultado.meta.totalContados ?? current.totalContados),
                    totalNaoContados: Number(resultado.meta.totalNaoContados ?? current.totalNaoContados),
                }));
            }

            if (resultado?.estado) {
                setEstado((current) => ({
                    ...current,
                    ...resultado.estado,
                }));
            }

            await fetchItems();

            setFlashMessage?.(responseBody?.message || 'Contagens salvas com sucesso.', responseBody?.type || 'success');

            return resultado;
        } catch (error) {
            if (requestId !== saveCountsRequestIdRef.current) {
                return null;
            }

            setFlashMessage?.(
                error?.response?.data?.message || error?.message || 'Erro ao salvar as contagens do balanço.',
                error?.response?.data?.type || 'error'
            );

            return null;
        } finally {
            if (requestId === saveCountsRequestIdRef.current) {
                saveCountsLockRef.current = false;
                setIsSavingCounts(false);
            }
        }
    }, [estado.podeEditarContagem, contagensEditadas, MovimentoAPI, movimentoId, movimentoUuid, fetchItems, setFlashMessage]);

    const conferirItens = useCallback(
        async (itensSelecionados) => {
            if (checkItemsLockRef.current || saveCountsLockRef.current || applyBalanceLockRef.current) return null;

            if (estado.podeConferir !== true) {
                setFlashMessage?.('A conferência só pode começar depois que todos os itens forem contados.', 'warning');
                return null;
            }

            if (typeof MovimentoAPI?.conferirItensBalanco !== 'function') {
                setFlashMessage?.('A conferência dos itens do balanço não está configurada.', 'error');
                return null;
            }

            const selecionados = Array.isArray(itensSelecionados) ? itensSelecionados : [];

            if (selecionados.length === 0) {
                setFlashMessage?.('Selecione ao menos um item para conferência.', 'warning');
                return null;
            }

            const chavesEnviadas = new Set();
            const draftsEnviados = new Map();
            const itens = [];

            for (const item of selecionados) {
                const chave = item?.uuid ? `uuid:${item.uuid}` : item?.id ? `id:${item.id}` : null;

                if (!chave || chavesEnviadas.has(chave)) continue;

                const draft = contagensEditadas[chave] || null;
                const possuiCorrecao = Boolean(
                    draft &&
                    (Object.prototype.hasOwnProperty.call(draft, 'quantidade') ||
                        Object.prototype.hasOwnProperty.call(draft, 'colaboradorId') ||
                        Object.prototype.hasOwnProperty.call(draft, 'observacao'))
                );

                if (item?.conferido === true && !possuiCorrecao) continue;

                if (draft && Object.prototype.hasOwnProperty.call(draft, 'pessoaId')) {
                    setFlashMessage?.(
                        'Existe uma alteração de contador pendente. Salve as contagens antes de conferir os itens.',
                        'warning'
                    );
                    return null;
                }

                const itemPayload = {
                    id: item?.id ?? draft?.id ?? null,
                    uuid: item?.uuid ?? draft?.uuid ?? null,
                };

                if (!itemPayload.id && !itemPayload.uuid) continue;

                if (draft && Object.prototype.hasOwnProperty.call(draft, 'quantidade')) {
                    const quantidadeTexto = String(draft.quantidade ?? '').trim();

                    if (quantidadeTexto === '') {
                        setFlashMessage?.(
                            'Existem contagens vazias. Informe a quantidade ou restaure o valor anterior.',
                            'warning'
                        );
                        return null;
                    }

                    const quantidade = Number(quantidadeTexto.replace(',', '.'));

                    if (!Number.isFinite(quantidade) || quantidade < 0) {
                        setFlashMessage?.('Existe uma quantidade inválida na conferência.', 'warning');
                        return null;
                    }

                    itemPayload.quantidade = quantidade;
                }

                if (draft && Object.prototype.hasOwnProperty.call(draft, 'colaboradorId')) {
                    itemPayload.colaboradorId = draft.colaboradorId;
                }

                if (draft && Object.prototype.hasOwnProperty.call(draft, 'observacao')) {
                    itemPayload.observacao = draft.observacao;
                }

                chavesEnviadas.add(chave);
                itens.push(itemPayload);

                if (draft) {
                    draftsEnviados.set(chave, draft);
                }
            }

            if (itens.length === 0) {
                setFlashMessage?.('Os itens selecionados já estão conferidos e não possuem alterações.', 'warning');
                return null;
            }

            const requestId = ++checkItemsRequestIdRef.current;

            checkItemsLockRef.current = true;
            setIsCheckingItems(true);

            try {
                const response = await MovimentoAPI.conferirItensBalanco({
                    movimentoId,
                    movimentoUuid,
                    itens,
                });

                if (requestId !== checkItemsRequestIdRef.current) {
                    return null;
                }

                const responseBody = response?.data ?? response;
                const resultado = responseBody?.data ?? responseBody;

                setContagensEditadas((current) => {
                    const next = { ...current };

                    draftsEnviados.forEach((draftEnviado, chave) => {
                        if (current[chave] === draftEnviado) {
                            delete next[chave];
                        }
                    });

                    return next;
                });

                if (resultado?.meta) {
                    setMeta((current) => ({
                        ...current,
                        totalRowCount: Number(resultado.meta.totalItens ?? current.totalRowCount),
                        totalContados: Number(resultado.meta.totalItens ?? current.totalContados),
                        totalNaoContados: 0,
                        totalConferidos: Number(resultado.meta.totalConferidos ?? current.totalConferidos),
                        totalAguardandoConferencia: Number(
                            resultado.meta.totalAguardandoConferencia ?? current.totalAguardandoConferencia
                        ),
                    }));
                }

                if (resultado?.estado) {
                    setEstado((current) => ({
                        ...current,
                        ...resultado.estado,
                    }));
                }

                await fetchItems();

                setFlashMessage?.(
                    responseBody?.message || 'Itens do balanço conferidos com sucesso.',
                    responseBody?.type || 'success'
                );

                return resultado;
            } catch (error) {
                if (requestId !== checkItemsRequestIdRef.current) {
                    return null;
                }

                setFlashMessage?.(
                    error?.response?.data?.message || error?.message || 'Erro ao conferir os itens do balanço.',
                    error?.response?.data?.type || 'error'
                );

                return null;
            } finally {
                if (requestId === checkItemsRequestIdRef.current) {
                    checkItemsLockRef.current = false;
                    setIsCheckingItems(false);
                }
            }
        },
        [estado.podeConferir, contagensEditadas, MovimentoAPI, movimentoId, movimentoUuid, fetchItems, setFlashMessage]
    );

    const aplicarBalanco = useCallback(async () => {
        if (applyBalanceLockRef.current || saveCountsLockRef.current || checkItemsLockRef.current) return null;

        if (estado.podeAplicar !== true) {
            setFlashMessage?.('O balanço precisa estar totalmente conferido antes da aplicação.', 'warning');
            return null;
        }

        if (Object.keys(contagensEditadas).length > 0) {
            setFlashMessage?.('Existem alterações pendentes na grid. Salve ou confira os itens antes de aplicar.', 'warning');
            return null;
        }

        if (typeof MovimentoAPI?.aplicarBalanco !== 'function') {
            setFlashMessage?.('A aplicação do balanço não está configurada.', 'error');
            return null;
        }

        if (!movimentoId && !movimentoUuid) {
            setFlashMessage?.('O movimento do balanço não foi identificado.', 'warning');
            return null;
        }

        const requestId = ++applyBalanceRequestIdRef.current;

        applyBalanceLockRef.current = true;
        setIsApplyingBalance(true);

        try {
            const response = await MovimentoAPI.aplicarBalanco({
                movimentoId,
                movimentoUuid,
            });

            if (requestId !== applyBalanceRequestIdRef.current) {
                return null;
            }

            const responseBody = response?.data ?? response;
            const resultado = responseBody?.data ?? responseBody;

            if (resultado?.meta) {
                setMeta((current) => {
                    const totalItens = Number(resultado.meta.totalItens ?? current.totalRowCount);

                    return {
                        ...current,
                        totalRowCount: totalItens,
                        totalContados: totalItens,
                        totalNaoContados: 0,
                        totalConferidos: totalItens,
                        totalAguardandoConferencia: 0,
                        totalAjustes: Number(resultado.meta.totalAjustes ?? 0),
                        totalSemDiferenca: Number(resultado.meta.totalSemDiferenca ?? 0),
                    };
                });
            }

            if (resultado?.estado) {
                setEstado((current) => ({
                    ...current,
                    ...resultado.estado,
                }));
            }

            if (resultado?.movimento?.situacaoId && typeof setDados === 'function') {
                setDados((current) => ({
                    ...current,
                    situacaoId: resultado.movimento.situacaoId,
                }));
            }

            await fetchItems();

            setFlashMessage?.(
                responseBody?.message || 'Balanço aplicado e fechado com sucesso.',
                responseBody?.type || 'success'
            );

            return resultado;
        } catch (error) {
            if (requestId !== applyBalanceRequestIdRef.current) {
                return null;
            }

            setFlashMessage?.(
                error?.response?.data?.message || error?.message || 'Erro ao aplicar o balanço.',
                error?.response?.data?.type || 'error'
            );

            return null;
        } finally {
            if (requestId === applyBalanceRequestIdRef.current) {
                applyBalanceLockRef.current = false;
                setIsApplyingBalance(false);
            }
        }
    }, [estado.podeAplicar, contagensEditadas, MovimentoAPI, movimentoId, movimentoUuid, setDados, fetchItems, setFlashMessage]);

    useEffect(() => {
        requestIdRef.current += 1;
        hasLoadedRef.current = false;
        setItems([]);
        setMeta({ ...META_INICIAL, size: initialPageSize });
        setEstado(ESTADO_INICIAL);
        setPagination({
            pageIndex: 0,
            pageSize: Number(initialPageSize) || 50,
        });
        setContagensEditadas({});
        saveCountsRequestIdRef.current += 1;
        saveCountsLockRef.current = false;
        setIsSavingCounts(false);
        checkItemsRequestIdRef.current += 1;
        checkItemsLockRef.current = false;
        setIsCheckingItems(false);
        applyBalanceRequestIdRef.current += 1;
        applyBalanceLockRef.current = false;
        setIsApplyingBalance(false);
    }, [movimentoId, movimentoUuid, enabled, initialPageSize]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    useEffect(() => {
        if (movimentoId) return;

        setPreviewItems([]);
        setPreviewMeta({
            totalRowCount: 0,
        });
        setIsPreviewError(false);

        peopleRequestIdRef.current += 1;
        setPeopleOptions([]);
        setIsPeopleLoading(false);
        setIsPeopleError(false);
    }, [movimentoId, dados?.almoxarifadoId, configuracao?.categoria, configuracao?.descricao]);
    return {
        key: 'balanco',

        main: {
            state: dados,
        },

        list: {
            items,
            rowCount: Number(meta.totalRowCount || 0),
        },

        mrt: {
            state: {
                pagination,
            },
            setters: {
                setPagination,
            },
            bind: {
                onPaginationChange: setPagination,
            },
            flags: {
                isLoading,
                isRefetching,
                isError,
            },
        },

        flags: {
            isLoading,
            isRefetching,
            isError,
            isSavingCounts,
            isCheckingItems,
            isApplyingBalance,
        },

        state: {
            meta,
            estado,
            configuracao,
        },
        draft: {
            contagens: contagensEditadas,
            totalAlterados: Object.keys(contagensEditadas).length,
            totalContagens: Object.values(contagensEditadas).filter((item) =>
                Object.prototype.hasOwnProperty.call(item, 'quantidade')
            ).length,
        },
        actions: {
            refresh: fetchItems,
            changeConfiguration,
            changeCounter,
            changeChecker,
            changeCount,
            preview,
            searchPeople,
            saveCounts,
            conferirItens,
            aplicarBalanco,
        },
        preview: {
            items: previewItems,
            meta: previewMeta,
            flags: {
                isLoading: isPreviewLoading,
                isError: isPreviewError,
            },
        },
        lookup: {
            people: {
                options: peopleOptions,
                flags: {
                    isLoading: isPeopleLoading,
                    isError: isPeopleError,
                },
            },
        },
    };
}
